import React, { memo, useCallback, useMemo, useRef, useState } from "react";
import classNames from "classnames";

import { ALL_REACTION_TYPES } from "components/ReactionPicker";
import { api } from "store";
import styles from "./styles.module.scss";

interface IReactionSummary {
	reactionsSummary: { type: string; count: number }[];
	recentUsers?: { id: number; full_name: string; first_name?: string }[];
	totalCount?: number;
	myReaction?: string | null;
	currentUserId?: number;
	onShowReactors?: (filterType?: string) => void;
	onReact?: (type: string) => void;
	renderAfterBadges?: React.ReactNode;
	reactableType?: string;
	reactableId?: number;
	isPost?: boolean;
}

const MAX_BADGES = 3;
const MAX_NAMES = 2;

// Badge that lazy-fetches reactor names on hover for tooltip
const ReactionBadge: React.FC<{
	r: { type: string; count: number };
	reaction: { type: string; label: string; emoji: string };
	isActive: boolean;
	hasUserReacted: boolean;
	hasClick: boolean;
	isComment: boolean;
	currentUserId?: number;
	onClick: () => void;
	reactableType?: string;
	reactableId?: number;
}> = ({ r, reaction, isActive, hasUserReacted, hasClick, isComment, currentUserId, onClick, reactableType, reactableId }) => {
	const [tooltip, setTooltip] = useState<string | null>(null);
	const fetched = useRef(false);

	const handleMouseEnter = useCallback(async () => {
		if (fetched.current || !reactableType || !reactableId) return;
		fetched.current = true;
		try {
			const response = await api.reactions.getReactors({
				reactable_type: reactableType,
				reactable_id: reactableId,
				reaction_type: r.type,
				limit: 3,
			});
			const resData = response?.data?.data ?? response?.data;
			const users: { id: number; full_name: string; first_name?: string }[] = resData?.users ?? [];
			if (users.length === 0) return;

			// Sort current user to the front so "You" shows first
			const sortedUsers = currentUserId != null
				// eslint-disable-next-line eqeqeq
				? [...users].sort((a, b) => (a.id == currentUserId ? -1 : b.id == currentUserId ? 1 : 0))
				: users;

			const names = sortedUsers
				.slice(0, 2)
				.map((u) => {
					// eslint-disable-next-line eqeqeq
					if (currentUserId != null && u.id == currentUserId) return "You";
					return u.first_name || u.full_name?.split(" ")[0] || "Member";
				});
			const remaining = r.count - names.length;

			let text: string;
			if (remaining > 0) {
				text = `${names.join(", ")} and ${remaining} other${remaining > 1 ? "s" : ""} reacted with ${reaction.emoji}`;
			} else if (names.length > 1) {
				text = `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]} reacted with ${reaction.emoji}`;
			} else {
				text = `${names[0]} reacted with ${reaction.emoji}`;
			}
			setTooltip(text);
		} catch {
			// Fallback
			setTooltip(`${r.count} ${reaction.label} reaction${r.count > 1 ? "s" : ""}`);
		}
	}, [r.type, r.count, reaction, reactableType, reactableId, currentUserId]);

	const tooltipText = tooltip ?? `${r.count} ${reaction.label} reaction${r.count > 1 ? "s" : ""}`;

	return (
		<div
			className={classNames(styles.badge, styles.badgeWithTooltip, {
				[styles.active]: isActive,
				[styles.hasReacted]: hasUserReacted,
				[styles.clickable]: hasClick,
				[styles.commentSize]: isComment,
			})}
			onClick={onClick}
			onMouseEnter={handleMouseEnter}
			role="button"
			tabIndex={0}
			aria-label={`${reaction.label} reaction, ${r.count}`}
			data-tooltip={tooltipText}
		>
			<span className={styles.badgeEmoji}>{reaction.emoji}</span>
			<span className={styles.badgeCount}>{r.count}</span>
		</div>
	);
};

const ReactionSummary: React.FunctionComponent<IReactionSummary> = ({
	reactionsSummary,
	recentUsers = [],
	totalCount,
	myReaction = null,
	currentUserId,
	onShowReactors,
	onReact,
	renderAfterBadges,
	reactableType,
	reactableId,
	isPost = true,
}) => {
	const sortedReactions = useMemo(
		() =>
			reactionsSummary
				.filter((r) => r.count > 0)
				.sort((a, b) => b.count - a.count),
		[reactionsSummary],
	);

	const total =
		totalCount ?? sortedReactions.reduce((sum, r) => sum + r.count, 0);

	const visibleBadges = sortedReactions.slice(0, MAX_BADGES);
	const overflowCount = sortedReactions.length - MAX_BADGES;

	const summaryText = useMemo(() => {
		if (total === 0) return "";

		const names: string[] = [];
		const isCurrentUserReacted = myReaction !== null;

		if (isCurrentUserReacted) {
			names.push("You");
		}

		for (const u of recentUsers) {
			if (names.length >= MAX_NAMES) break;
			// eslint-disable-next-line eqeqeq
			if (currentUserId != null && u.id == currentUserId) continue;
			if (!currentUserId && isCurrentUserReacted && recentUsers.indexOf(u) === 0) continue;
			const firstName = u.first_name || u.full_name?.split(" ")[0] || "Member";
			names.push(firstName);
		}

		const othersCount = total - names.length;

		if (names.length === 0) {
			return `${total} reaction${total > 1 ? "s" : ""}`;
		}

		if (othersCount > 0) {
			return `${names.join(", ")} and ${othersCount} other${othersCount > 1 ? "s" : ""}`;
		}

		if (names.length > 1) {
			return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
		}

		return names[0];
	}, [recentUsers, total, myReaction, currentUserId]);

	if (total === 0) {
		return renderAfterBadges ? <>{renderAfterBadges}</> : null;
	}

	return (
		<>
			<div className={classNames(styles.badgeContainer, { [styles.commentSize]: !isPost })}>
				{visibleBadges.map((r) => {
					const reaction = ALL_REACTION_TYPES.find((rx) => rx.type === r.type);
					if (!reaction) return null;
					const isActive = myReaction === r.type;
					return (
						<ReactionBadge
							key={r.type}
							r={r}
							reaction={reaction}
							isActive={isActive}
							hasUserReacted={!!myReaction}
							hasClick={isActive ? !!onReact : !!onShowReactors}
							isComment={!isPost}
							currentUserId={currentUserId}
							onClick={() => isActive ? onReact?.(r.type) : onShowReactors?.(r.type)}
							reactableType={reactableType}
							reactableId={reactableId}
						/>
					);
				})}
				{overflowCount > 0 && (
					<div
						className={classNames(styles.overflowBadge, { [styles.commentSize]: !isPost })}
						onClick={() => onShowReactors?.()}
						role="button"
						tabIndex={0}
					>
						+{overflowCount}
					</div>
				)}
				{renderAfterBadges}
			</div>
			{summaryText && (
				<span
					className={styles.reactorNames}
					onClick={() => onShowReactors?.()}
				>
					{summaryText}
				</span>
			)}
		</>
	);
};

export default memo(ReactionSummary);
